import React from 'react';
import { Modal, Table, TableColumnProps, Empty, Tag, Typography } from 'antd';
import { TimeWindow } from 'interfaces/journey';
import { FlaggedShipment } from 'services/journeys/shipmentHelpers';
import dayjs from 'util/dayjs';

interface OfferAtRiskModalProps {
  open: boolean;
  items: FlaggedShipment[];
  routeSchedule?: TimeWindow;
  confirmLoading?: boolean;
  hasDriver?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

const OfferAtRiskModal: React.FC<OfferAtRiskModalProps> = ({
  open,
  items,
  routeSchedule,
  confirmLoading = false,
  hasDriver = false,
  onCancel,
  onConfirm,
}) => {
  const groupItems = items.filter((item) => item.issue?.blame === 'group');
  const shipmentItems = items.filter((item) => item.issue?.blame !== 'group');

  const columns: TableColumnProps<FlaggedShipment>[] = [
    {
      title: 'Envío',
      dataIndex: 'externalCode',
      key: 'externalCode',
      width: 120,
      render: (code: string | undefined) => code || '-',
    },
    {
      title: 'Ventana de entrega',
      dataIndex: 'schedule',
      key: 'schedule',
      width: 110,
      render: (shipmentSchedule?: TimeWindow) =>
        shipmentSchedule
          ? `${shipmentSchedule.from} - ${shipmentSchedule.to}`
          : '-',
    },
    {
      title: 'Motivo',
      key: 'reason',
      render: (_, record) => record.issue?.reasonDescription || '-',
    },
  ];

  const okText = hasDriver ? 'Finalizar y ofrecer' : 'Finalizar y ofertar';
  const cancelText = 'Seguir operando';

  const routeDate = routeSchedule?.date
    ? dayjs(routeSchedule.date).format('DD/MM/YYYY')
    : undefined;

  return (
    <Modal
      title={
        <>
          Paquetes con problemas
          {routeSchedule && (
            <Tag color="orange" style={{ marginLeft: 8 }}>
              {routeDate} {routeSchedule.from} → {routeSchedule.to}
            </Tag>
          )}
        </>
      }
      open={open}
      onOk={onConfirm}
      onCancel={onCancel}
      okText={okText}
      cancelText={cancelText}
      confirmLoading={confirmLoading}
      width={700}
    >
      {items.length === 0 && <Empty description="No hay paquetes en riesgo" />}
      {shipmentItems.length > 0 && (
        <>
          <Typography.Paragraph type="secondary" style={{ marginBottom: 8 }}>
            Los siguientes paquetes no van a entregarse dentro de su ventana
            horaria. Por favor revisar si quieren mantenerlos dentro de la ruta
            o retirarlos.
          </Typography.Paragraph>
          <Table
            columns={columns}
            dataSource={shipmentItems.map((item, idx) => ({
              ...item,
              key: idx,
            }))}
            pagination={false}
            size="small"
            tableLayout="fixed"
            scroll={{ y: 220 }}
          />
        </>
      )}
      {groupItems.length > 0 && (
        <>
          <Typography.Paragraph
            type="secondary"
            style={{ color: '#D48806', marginBottom: 8, marginTop: 16 }}
          >
            Los siguientes paquetes generan conflictos, causando que otros
            envíos puedan no llegar a tiempo. Removiéndolos se mejora el
            cumplimiento de los horarios del resto de la ruta.
          </Typography.Paragraph>
          <Table
            columns={columns}
            dataSource={groupItems.map((item, idx) => ({ ...item, key: idx }))}
            pagination={false}
            size="small"
            tableLayout="fixed"
            showHeader={shipmentItems.length === 0}
            scroll={{ y: 220 }}
          />
        </>
      )}
    </Modal>
  );
};

export default OfferAtRiskModal;
